/**
 * App 详情落地页（仿应用商店风格，仅 web 端 #/download 可见）
 * - 元数据来自 public/app-meta.json（运行时 fetch；改版本/换图只替换文件，不动代码）
 * - 用途：支付宝 APP 支付送审的「APP 下载链接」与「APP 上架信息截图」
 * - 资源路径统一用 import.meta.env.BASE_URL 拼接，兼容子路径 /ziwei-paipan/ 部署
 * - JSON 拉取失败降级为旧版极简引导（读 VITE_APP_DOWNLOAD_URL 兜底，向后兼容）
 */
import { useEffect, useState } from 'react';

type Screenshot = { src: string; caption?: string };
type AppMeta = {
  name: string;
  developer?: string;
  category?: string;
  versionName?: string;
  versionCode?: number;
  sizeMB?: number;
  updateDate?: string;
  description?: string;
  features?: string[];
  icon?: string;
  screenshots?: Screenshot[];
  android?: { downloadUrl?: string; minSdk?: number };
  ios?: { available?: boolean };
};

const FALLBACK_DOWNLOAD_URL = (import.meta.env.VITE_APP_DOWNLOAD_URL as string | undefined)?.trim() || '';

/** 相对路径 → 带部署 base 的绝对 URL（去重前导斜杠） */
function assetUrl(p?: string): string {
  if (!p) return '';
  return `${import.meta.env.BASE_URL}${p.replace(/^\//, '')}`;
}

/** 功能勾选图标（SVG，遵循移动端禁 emoji 约定） */
function CheckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-0.5 shrink-0 text-emerald-500"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export default function AppDownloadPage() {
  const [meta, setMeta] = useState<AppMeta | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${import.meta.env.BASE_URL}app-meta.json`, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as AppMeta;
        if (!cancelled) {
          setMeta(data);
          setStatus('ok');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 降级：JSON 拉取失败 → 旧版极简引导（尽量仍给出下载入口）
  if (status === 'error') {
    return (
      <div className="mx-auto max-w-md py-10">
        <div className="rounded-lg bg-white px-6 py-8 text-center shadow">
          <h1 className="text-xl font-semibold text-slate-900">元紫 · 紫微斗数</h1>
          <p className="mt-3 text-sm leading-6 text-slate-500">
            网页版提供免费的紫微排盘服务。
            <br />
            知识学习、AI 断盘、会员等功能请在 App 内使用。
          </p>
          {FALLBACK_DOWNLOAD_URL ? (
            <a
              href={FALLBACK_DOWNLOAD_URL}
              className="mt-6 block rounded-md bg-slate-800 px-4 py-3 text-sm font-medium text-white hover:bg-slate-700"
            >
              下载 Android App
            </a>
          ) : (
            <div className="mt-6 rounded-md bg-slate-100 px-4 py-3 text-sm text-slate-400">Android 下载即将上线</div>
          )}
        </div>
      </div>
    );
  }

  if (status === 'loading' || !meta) {
    return (
      <div className="mx-auto max-w-md animate-pulse py-6">
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-2xl bg-slate-200" />
          <div className="flex-1 space-y-2">
            <div className="h-5 w-2/3 rounded bg-slate-200" />
            <div className="h-4 w-1/3 rounded bg-slate-200" />
          </div>
        </div>
        <div className="mt-6 h-40 rounded-lg bg-slate-200" />
      </div>
    );
  }

  const downloadUrl = meta.android?.downloadUrl?.trim() || FALLBACK_DOWNLOAD_URL;
  const iosAvailable = meta.ios?.available ?? false;

  return (
    <div className="mx-auto max-w-md pb-10">
      {/* 头部：图标 + 名称 + 开发者 + 类别 */}
      <section className="rounded-xl bg-white px-5 py-5 shadow">
        <div className="flex items-center gap-4">
          {meta.icon ? (
            <img src={assetUrl(meta.icon)} alt={meta.name} className="h-20 w-20 rounded-2xl object-cover" />
          ) : (
            <div className="h-20 w-20 rounded-2xl bg-slate-200" />
          )}
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold text-slate-900">{meta.name}</h1>
            {meta.developer ? <p className="mt-1 truncate text-sm text-slate-500">{meta.developer}</p> : null}
            {meta.category ? (
              <span className="mt-2 inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-500">
                {meta.category}
              </span>
            ) : null}
          </div>
        </div>

        {/* 元信息徽标行 */}
        <div className="mt-4 flex items-center divide-x divide-slate-200 border-t border-slate-100 pt-4 text-center">
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-800">{meta.versionName ? `v${meta.versionName}` : '—'}</div>
            <div className="mt-0.5 text-xs text-slate-400">版本</div>
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-800">{meta.sizeMB ? `${meta.sizeMB} MB` : '—'}</div>
            <div className="mt-0.5 text-xs text-slate-400">大小</div>
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-800">{meta.updateDate ?? '—'}</div>
            <div className="mt-0.5 text-xs text-slate-400">更新</div>
          </div>
        </div>
      </section>

      {/* 下载按钮 */}
      <section className="mt-4 space-y-3">
        {downloadUrl ? (
          <a
            href={downloadUrl}
            download
            className="block rounded-lg bg-slate-800 px-4 py-3.5 text-center text-sm font-medium text-white hover:bg-slate-700 active:bg-slate-900"
          >
            下载 Android App
          </a>
        ) : (
          <div className="rounded-lg bg-slate-100 px-4 py-3.5 text-center text-sm text-slate-400">Android 下载即将上线</div>
        )}
        <div className="rounded-lg border border-slate-200 px-4 py-3 text-center text-sm text-slate-400">
          {iosAvailable ? 'iOS 版本' : 'iOS 版本暂未提供'}
        </div>
      </section>

      {/* 应用截图：横向滚动 snap */}
      {meta.screenshots?.length ? (
        <section className="mt-6">
          <h2 className="mb-2 px-1 text-base font-semibold text-slate-800">应用截图</h2>
          <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2">
            {meta.screenshots.map((s) => (
              <figure key={s.src} className="shrink-0 snap-start">
                <img
                  src={assetUrl(s.src)}
                  alt={s.caption ?? ''}
                  loading="lazy"
                  className="h-[420px] w-[210px] rounded-xl border border-slate-100 object-cover shadow-sm"
                />
                {s.caption ? <figcaption className="mt-1.5 text-center text-xs text-slate-400">{s.caption}</figcaption> : null}
              </figure>
            ))}
          </div>
        </section>
      ) : null}

      {/* 应用简介 */}
      {meta.description ? (
        <section className="mt-6 rounded-xl bg-white px-5 py-4 shadow">
          <h2 className="mb-2 text-base font-semibold text-slate-800">应用简介</h2>
          <p className="text-sm leading-6 text-slate-600">{meta.description}</p>
        </section>
      ) : null}

      {/* 核心功能 */}
      {meta.features?.length ? (
        <section className="mt-4 rounded-xl bg-white px-5 py-4 shadow">
          <h2 className="mb-3 text-base font-semibold text-slate-800">核心功能</h2>
          <ul className="space-y-2.5">
            {meta.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm leading-6 text-slate-600">
                <CheckIcon />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <p className="mt-6 px-2 text-center text-xs leading-5 text-slate-400">
        本 App 通过官网直接分发，暂未上架第三方应用商店。
      </p>
    </div>
  );
}
